import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import {
  X,
  Sparkles,
  HardDrive,
  IndianRupee,
  Loader2,
  ShoppingCart,
} from "lucide-react";
import {
  createStorageAddonOrder,
  getApiErrorMessage,
  getStorageAddonPricing,
  quoteStorageAddon,
  verifyStorageAddonOrder,
} from "../../../../Redux/ApiCalls/Dashboard/StorageAddonAPI";
import {
  StorageAddonPricing,
  StorageAddonQuote,
} from "../../../../Data/storageAddon.dto";
import { loadCashfree, getCashfreeMode } from "../../../../Utils/cashfree";
import {
  showErrorToast,
  showSuccessToast,
} from "../../Utlis/Toast";

interface BuyStorageModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const QUOTE_DEBOUNCE_MS = 400;

const formatINR = (amount: string | number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(amount));

const BuyStorageModal: React.FC<BuyStorageModalProps> = ({
  onClose,
  onSuccess,
}) => {
  const [pricing, setPricing] = useState<StorageAddonPricing | null>(null);
  const [gb, setGb] = useState("");
  const [quote, setQuote] = useState<StorageAddonQuote | null>(null);
  const [loadError, setLoadError] = useState("");
  const [quoteError, setQuoteError] = useState("");
  const [loadingPricing, setLoadingPricing] = useState(true);
  const [quoting, setQuoting] = useState(false);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingPricing(true);
      setLoadError("");
      try {
        const data = await getStorageAddonPricing();
        if (cancelled) return;
        setPricing(data.pricing);
        if (!data.pricing?.is_active) {
          setLoadError("Extra storage purchase is not available right now.");
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(
            getApiErrorMessage(err, "Could not load storage pricing.")
          );
        }
      } finally {
        if (!cancelled) setLoadingPricing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!pricing?.is_active) return;
    const value = parseInt(gb, 10);
    if (!gb || isNaN(value) || value <= 0) {
      setQuote(null);
      setQuoteError("");
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setQuoting(true);
      setQuoteError("");
      try {
        const data = await quoteStorageAddon(value);
        if (!cancelled) setQuote(data);
      } catch (err) {
        if (!cancelled) {
          setQuote(null);
          setQuoteError(getApiErrorMessage(err, "Could not calculate cost."));
        }
      } finally {
        if (!cancelled) setQuoting(false);
      }
    }, QUOTE_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [gb, pricing?.is_active]);

  const handlePay = async () => {
    const value = parseInt(gb, 10);
    if (!value || paying || !pricing?.is_active) return;

    setPaying(true);
    try {
      const returnUrl = `${window.location.origin}/dashboard/subscriptions`;
      const order = await createStorageAddonOrder({
        storage_gb: value,
        return_url: returnUrl,
      });

      if (!order?.payment_session_id || !order?.order_id) {
        throw new Error("Payment session missing. Try again.");
      }

      const mode = getCashfreeMode();
      const cashfree = await loadCashfree(mode);
      const checkoutResult = await cashfree.checkout({
        paymentSessionId: order.payment_session_id,
        redirectTarget: "_modal",
      });

      if (checkoutResult?.error) {
        throw new Error(
          checkoutResult.error?.message || "Cashfree payment failed"
        );
      }

      const paymentStatus =
        checkoutResult?.paymentDetails?.paymentStatus ||
        checkoutResult?.paymentDetails?.payment_status;

      if (paymentStatus && paymentStatus !== "SUCCESS") {
        throw new Error("Payment not completed");
      }

      await verifyStorageAddonOrder(order.order_id);
      showSuccessToast(
        `${value} GB extra storage added to your plan.`
      );
      onSuccess();
      onClose();
    } catch (err: any) {
      showErrorToast(getApiErrorMessage(err, "Storage purchase failed."));
    } finally {
      setPaying(false);
    }
  };

  const gbNum = parseInt(gb, 10);
  const canPay =
    !!pricing?.is_active &&
    !!quote &&
    !quoting &&
    !paying &&
    !quoteError &&
    !!gbNum &&
    gbNum > 0;

  return (
    <Overlay onClick={onClose}>
      <ModalBox onClick={(e) => e.stopPropagation()}>
        <ModalHero>
          <HeroGlow />
          <HeroTop>
            <HeroIcon>
              <HardDrive size={22} />
            </HeroIcon>
            <HeroText>
              <HeroBadge>
                <Sparkles size={11} />
                Extra storage
              </HeroBadge>
              <ModalTitle>Buy more storage</ModalTitle>
              <ModalSubtitle>
                Add GB to your plan. Price is charged per GB.
              </ModalSubtitle>
            </HeroText>
            <CloseBtn type="button" onClick={onClose} aria-label="Close" disabled={paying}>
              <X size={18} />
            </CloseBtn>
          </HeroTop>
        </ModalHero>

        <ModalBody>
          {loadingPricing ? (
            <Center>
              <SpinIcon size={24} />
              Loading pricing…
            </Center>
          ) : loadError ? (
            <ErrorBox role="alert">{loadError}</ErrorBox>
          ) : (
            <>
              <PriceChip>
                <IndianRupee size={14} />
                <span>
                  <strong>{formatINR(pricing?.price_per_gb ?? 0)}</strong> / GB
                </span>
              </PriceChip>

              {pricing?.description && (
                <Hint>{pricing.description}</Hint>
              )}

              <Field>
                <Label htmlFor="storage-gb">Storage to buy (GB)</Label>
                <InputWrap>
                  <HardDrive size={16} color="#c94daa" />
                  <Input
                    id="storage-gb"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={gb}
                    maxLength={5}
                    disabled={paying}
                    placeholder={`Min ${pricing?.min_gb ?? 1}, max ${pricing?.max_gb ?? 1000}`}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      setGb(val);
                    }}
                  />
                </InputWrap>
                <RangeHint>
                  Allowed range: {pricing?.min_gb ?? 1}–{pricing?.max_gb ?? 1000} GB
                  {pricing?.validity_days
                    ? ` · Valid ${pricing.validity_days} days`
                    : " · Permanent add-on"}
                </RangeHint>
              </Field>

              {(quoting || quote || quoteError) && (
                <QuoteCard>
                  {quoting ? (
                    <QuoteMuted>
                      <SpinIcon size={14} /> Calculating…
                    </QuoteMuted>
                  ) : quoteError ? (
                    <ErrorBox role="alert">{quoteError}</ErrorBox>
                  ) : quote ? (
                    <>
                      <QuoteRow>
                        <span>Storage</span>
                        <strong>{quote.storage_size || `${quote.storage_gb} GB`}</strong>
                      </QuoteRow>
                      <QuoteRow>
                        <span>Rate</span>
                        <strong>{formatINR(quote.price_per_gb)} / GB</strong>
                      </QuoteRow>
                      <QuoteRow $total>
                        <span>Total</span>
                        <strong>{formatINR(quote.total_amount)}</strong>
                      </QuoteRow>
                    </>
                  ) : null}
                </QuoteCard>
              )}
            </>
          )}
        </ModalBody>

        <ActionRow>
          <CancelBtn type="button" onClick={onClose} disabled={paying}>
            Cancel
          </CancelBtn>
          <PayBtn type="button" disabled={!canPay} onClick={handlePay}>
            {paying ? (
              <>
                <SpinIcon size={16} />
                Processing…
              </>
            ) : (
              <>
                <ShoppingCart size={16} />
                Pay{quote ? ` ${formatINR(quote.total_amount)}` : ""}
              </>
            )}
          </PayBtn>
        </ActionRow>
      </ModalBox>
    </Overlay>
  );
};

export default BuyStorageModal;

const GRADIENT =
  "linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const SpinIcon = styled(Loader2)`
  animation: ${spin} 0.8s linear infinite;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
`;

const ModalBox = styled.div`
  width: 100%;
  max-width: 480px;
  max-height: 92vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 22px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);
`;

const ModalHero = styled.div`
  position: relative;
  overflow: hidden;
  padding: 22px 24px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.14) 0%,
    rgba(206, 86, 176, 0.1) 55%,
    rgba(53, 183, 200, 0.06) 100%
  );
  border-bottom: 1px solid rgba(201, 77, 170, 0.1);
  flex-shrink: 0;
`;

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -50px;
  right: -20px;
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 201, 81, 0.25) 0%, transparent 70%);
`;

const HeroTop = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  gap: 14px;
`;

const HeroIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${GRADIENT};
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 6px 16px rgba(201, 77, 170, 0.25);
`;

const HeroText = styled.div`
  flex: 1;
  min-width: 0;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 4px;
  padding: 3px 9px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: #c94daa;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 22px;
  font-weight: 800;
  color: #171717;
  line-height: 1.2;
`;

const ModalSubtitle = styled.p`
  margin: 4px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.58);
`;

const CloseBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(23, 23, 23, 0.6);
  flex-shrink: 0;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ModalBody = styled.div`
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow-y: auto;
  min-height: 0;
`;

const Center = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 28px 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: rgba(23, 23, 23, 0.55);
`;

const PriceChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(201, 77, 170, 0.08);
  border: 1px solid rgba(201, 77, 170, 0.16);
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #c94daa;
`;

const Hint = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.55);
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.55);
`;

const InputWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  padding: 0 14px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 12px;
  &:focus-within {
    border-color: rgba(201, 77, 170, 0.4);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
    background: #fff;
  }
`;

const Input = styled.input`
  width: 100%;
  border: none;
  background: transparent;
  font-family: Urbanist, sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #171717;
  &:focus {
    outline: none;
  }
  &::placeholder {
    color: rgba(23, 23, 23, 0.35);
    font-weight: 500;
    font-size: 13px;
  }
  &:disabled {
    opacity: 0.6;
  }
`;

const RangeHint = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.45);
`;

const QuoteCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px;
  border-radius: 14px;
  border: 1px solid rgba(201, 77, 170, 0.14);
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.06) 0%,
    rgba(206, 86, 176, 0.05) 100%
  );
`;

const QuoteRow = styled.div<{ $total?: boolean }>`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.65);

  strong {
    font-family: Urbanist, sans-serif;
    font-weight: 700;
    color: #171717;
  }

  ${({ $total }) =>
    $total &&
    `
    margin-top: 4px;
    padding-top: 8px;
    border-top: 1px dashed rgba(23, 23, 23, 0.12);
    font-weight: 700;
    color: #171717;
    strong { color: #31a66a; font-size: 16px; }
  `}
`;

const QuoteMuted = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  color: rgba(23, 23, 23, 0.5);
`;

const ErrorBox = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #e44862;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 10px;
  padding: 16px 24px 22px;
  border-top: 1px solid rgba(23, 23, 23, 0.06);
  flex-shrink: 0;
`;

const CancelBtn = styled.button`
  padding: 0 18px;
  height: 44px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  background: #fff;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.65);
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PayBtn = styled.button`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  border: none;
  border-radius: 12px;
  background: ${GRADIENT};
  color: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(201, 77, 170, 0.25);
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none;
  }
`;
